import { useEffect, useRef, useCallback, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { useWorkOrdersStore } from '../../store/workOrdersStore';
import { getWorkOrders } from '../../utils/api';
import { 
  getWorkOrders as getOfflineWorkOrders,
  saveWorkOrders
} from '../../utils/offlineDb';
import { AddressList } from './AddressList';
import { WorkOrderList } from './WorkOrderList';
import { RefreshCw, Briefcase, CheckCircle2, Wifi, WifiOff } from 'lucide-react';
import { ErrorState } from '../common/ErrorState';

export function WorkOrdersView() {
  const navigate = useNavigate();
  const location = useLocation();
  const scrollPositionRef = useRef<number>(0);
  const fetchingRef = useRef<boolean>(false);
  const { accessToken, workerId, isAuthenticated } = useAuthStore();
  const {
    loading,
    setLoading,
    setError,
    setData,
    selectedAddress,
    error,
    setSelectedAddress,
  } = useWorkOrdersStore();
  const [showCompleted, setShowCompleted] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Reset selected address when navigating away
  useEffect(() => {
    return () => {
      setSelectedAddress(null);
    };
  }, [location.pathname]);

  // Save scroll position when leaving the page
  useEffect(() => {
    return () => {
      scrollPositionRef.current = window.scrollY;
    };
  }, [location.pathname]);

  const loadOfflineData = async () => {
    try {
      const offlineOrders = await getOfflineWorkOrders();
      console.log('Loaded offline orders:', offlineOrders?.length || 0);
      // Always set the orders, even if empty
      setData(offlineOrders);
      setError(null);
    } catch (error) {
      console.error('Failed to load offline data:', error);
      setError('Failed to load offline data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkOrders = useCallback(async () => {
    if (!accessToken || !workerId) {
      setError('Please log in to view work orders');
      navigate('/login');
      return;
    }

    // Prevent multiple simultaneous requests
    if (fetchingRef.current) {
      return;
    }

    fetchingRef.current = true;
    setLoading(true);

    try {
      if (!navigator.onLine) {
        await loadOfflineData();
        console.log('Loaded data from offline storage');
        return;
      }

      const response = await getWorkOrders(accessToken, workerId);
      console.log('Fetched orders from API:', response.orders?.length || 0);
      
      // Save orders to offline storage
      await saveWorkOrders(response.orders || []);
      console.log('Saved orders to offline storage');
      
      setData(response.orders);
      setError(null);
    } catch (error: any) {
      console.error('Get work orders error:', {
        message: error.message,
        workerId,
        timestamp: new Date().toISOString(),
      });
      
      // If network error, try loading offline data
      if (!navigator.onLine || error.message.includes('network')) {
        console.log('Network error, falling back to offline data');
        await loadOfflineData();
      } else {
        setError(error.message || 'Failed to fetch work orders');
      }
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [accessToken, workerId, navigate, setData, setError, setLoading]);

  // Restore scroll position and refresh data when returning to the page
  useEffect(() => {
    if (location.pathname === '/work-orders') {
      fetchWorkOrders().then(() => {
        window.scrollTo(0, scrollPositionRef.current);
      });
    }
  }, [location.pathname, fetchWorkOrders]);

  useEffect(() => {
    if (accessToken && workerId) {
      fetchWorkOrders();
    }
  }, [accessToken, workerId, fetchWorkOrders]);

  if (!isAuthenticated) {
    return null;
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <ErrorState 
          message={error} 
          onRetry={fetchWorkOrders}
        />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-2 text-sm">
          {isOffline ? (
            <div className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
              <WifiOff className="h-4 w-4" />
              <span>Offline Mode</span>
            </div>
          ) : (
            <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
              <Wifi className="h-4 w-4" />
              <span>Online</span>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {!selectedAddress && (
            <div className="inline-flex rounded-lg p-1 bg-gray-100 dark:bg-dark-card border border-light-border dark:border-dark-border">
              <button
                onClick={() => setShowCompleted(false)}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  !showCompleted
                    ? 'bg-white dark:bg-dark-border text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <Briefcase className="h-4 w-4" />
                Active
              </button>
              <button
                onClick={() => setShowCompleted(true)}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  showCompleted
                    ? 'bg-white dark:bg-dark-border text-primary-600 dark:text-primary-400 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <CheckCircle2 className="h-4 w-4" />
                Completed
              </button>
            </div>
          )}
          <button
            onClick={() => fetchWorkOrders()}
            disabled={loading}
            className="p-2 hover:bg-gray-100 dark:hover:bg-dark-border rounded-lg transition-colors disabled:opacity-50"
            title="Refresh work orders"
          >
            <RefreshCw className={`h-5 w-5 text-gray-600 dark:text-gray-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {selectedAddress ? (
        <WorkOrderList showCompleted={showCompleted} />
      ) : (
        <AddressList showCompleted={showCompleted} />
      )}
    </div>
  );
}